import { getCountryNameWithFlag } from "@repo/shared";
import { SUSTAINABILITY_EXCLUSION_KEYS } from "@repo/shared/types";
import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { LineChart } from "@/components/charts/line-chart";
import { HistoricalStatisticsPanel } from "@/components/historical-statistics";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBrandColor } from "@/lib/context/brand-color-context";
import {
  useInstrumentMetadata,
  useProductAnalysis,
  useProductXray,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { getRelativeTime } from "@/lib/utils";
import { EquityStyleGrid } from "@/pages/clients/detail/flows/instance/equity-style-grid";
import { XRayPieChart } from "@/pages/clients/detail/flows/instance/xray-pie-chart";
import { EXCLUSION_CATALOG } from "../sustainability/exclusion-catalog";
import { InstrumentSyncAlert } from "./instrument-sync-status";
import { T } from "./product-analysis-dialog.i18n";

type XrayView =
  | "asset_allocation"
  | "country"
  | "region"
  | "stock_sector"
  | "bond_sector"
  | "equity_style"
  | "top_holdings";

type View = "historical" | "statistics" | XrayView | "sustainability";

type ProductAnalysisDialogProps = {
  open: boolean;
  collectionId: string;
  productId: string | null;
  productName: string;
  isin: string | null;
  orderAmountType?: "amount" | "units";
  ticker?: string | null;
  dataset?: string | null;
  onClose: () => void;
};

export function ProductAnalysisDialog({
  open,
  collectionId,
  productId,
  productName,
  isin,
  orderAmountType = "amount",
  ticker = null,
  dataset = null,
  onClose,
}: ProductAnalysisDialogProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { formatDate } = useRegionalFormat();
  const brandColor = useBrandColor();
  const [view, setView] = useState<View>("historical");

  // Always reset to the Historical Return tab when (re)opening or switching product.
  useEffect(() => {
    if (open) setView("historical");
  }, [open, productId]);

  // Historical return and vendor sustainability data both come from the linked
  // instrument; without one those tabs can only ever render an empty state.
  const hasInstrument = !!(ticker && dataset);

  // Stats/timeseries (per product) and X-Ray (cached 24h per ISIN) are fetched
  // separately so the expensive Morningstar call is reused across products.
  // Stats are skipped without a linked instrument -- the Historical tab is hidden.
  const { data: analysis, isLoading: statsLoading } = useProductAnalysis(
    open && hasInstrument ? collectionId : null,
    open && hasInstrument ? productId : null,
  );
  const { data: xrayData, isLoading: xrayLoading } = useProductXray(
    open ? collectionId : null,
    open ? productId : null,
    isin,
    userLanguage,
  );
  // Linked-instrument metadata carries the raw vendor ESG data (SFDR + the two
  // sector maps) for the read-only Sustainability tab.
  const { metadata } = useInstrumentMetadata(
    open && ticker ? ticker : null,
    open && dataset ? dataset : null,
  );

  const isLoading = statsLoading || xrayLoading;
  const formatPct = (value: number) => (Number.isNaN(value) ? t.na : `${value.toFixed(2)}%`);

  const timeseries = analysis?.timeseries ?? [];
  const stats = analysis?.statistics ?? null;
  const xray = xrayData?.xray ?? null;

  const commonPieProps = {
    timestamp: xray?.timestamp,
    coverage: xray?.coverage,
    language: userLanguage,
    showChart: true,
    showTable: true,
  };

  const renderHistorical = () => {
    if (timeseries.length === 0) {
      return <div className="text-sm text-muted-foreground">{t.noStats}</div>;
    }
    return (
      <div className="space-y-2">
        <div className="flex gap-6 text-sm">
          <div>
            <span className="text-muted-foreground">{t.expectedReturn}: </span>
            <span className="font-medium">{stats ? formatPct(stats.expectedReturn) : t.na}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t.expectedVolatility}: </span>
            <span className="font-medium">
              {stats ? formatPct(stats.expectedVolatility) : t.na}
            </span>
          </div>
        </div>
        {/* LineChart falls back to a fixed width (createLineChartSpec ignores
            "container"), so pass an explicit wide width and center the SVG. */}
        <div className="[&_svg]:mx-auto">
          <LineChart
            data={timeseries}
            width={880}
            height={340}
            lang={userLanguage}
            primaryColor={brandColor ?? undefined}
            extraYGridlines={1}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          {analysis?.instrumentCurrency && (
            <span>
              {t.instrumentCurrency}: {analysis.instrumentCurrency}
            </span>
          )}
          {analysis && (
            <span>
              {t.calculatedCurrency}: {analysis.currencyIso}
            </span>
          )}
          {analysis?.lastPrice && (
            <span>
              {t.lastPrice}: {analysis.lastPrice.value.toFixed(2)}
              {analysis.lastPrice.currency ? ` ${analysis.lastPrice.currency}` : ""}
              {analysis.lastPrice.date ? ` (${analysis.lastPrice.date})` : ""}
            </span>
          )}
          <span>
            {t.orderType}: {orderAmountType === "units" ? t.orderUnits : t.orderAmount}
          </span>
          <span>
            {t.dataPoints}: {timeseries.length}
          </span>
        </div>
      </div>
    );
  };

  // QL-706 — the same data as the Historical Return chart (collection currency,
  // patch applied), from the monthly series.
  const renderStatistics = () => {
    const statistics = analysis?.historicalStatistics;
    if (!analysis || !statistics) {
      return <div className="text-sm text-muted-foreground">{t.noStats}</div>;
    }
    return (
      <HistoricalStatisticsPanel
        statistics={statistics}
        currency={analysis.currencyIso}
        source="calculated"
        measuredTo={analysis.measuredTo}
        footer={
          <>
            {analysis.instrumentCurrency && (
              <span>
                {t.instrumentCurrency}: {analysis.instrumentCurrency}
              </span>
            )}
            <span>
              {t.calculatedCurrency}: {analysis.currencyIso}
            </span>
            <span>
              {t.monthlyPoints}: {statistics.monthlyPoints}
            </span>
            {analysis.lastPrice && (
              <span>
                {t.lastPrice}: {analysis.lastPrice.value.toFixed(2)}
                {analysis.lastPrice.currency ? ` ${analysis.lastPrice.currency}` : ""}
                {analysis.lastPrice.date ? ` (${analysis.lastPrice.date})` : ""}
              </span>
            )}
          </>
        }
      />
    );
  };

  const renderXray = (v: XrayView) => {
    if (!xray) {
      return <div className="text-sm text-muted-foreground">{t.noXray}</div>;
    }
    switch (v) {
      case "asset_allocation": {
        const d = xray.assetAllocation.map((i) => ({
          id: i.assetClass,
          label: i.assetClass,
          value: i.percentage,
        }));
        return d.length ? (
          <XRayPieChart data={d} categoryLabel={t.assetAllocation} {...commonPieProps} />
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      }
      case "country": {
        const d = [...xray.countryExposure]
          .sort((a, b) => b.percentage - a.percentage)
          .slice(0, 10)
          .map((i) => ({
            id: i.countryCode,
            label: getCountryNameWithFlag(i.countryCode, userLanguage),
            value: i.percentage,
          }));
        return d.length ? (
          <XRayPieChart data={d} categoryLabel={t.country} {...commonPieProps} />
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      }
      case "region": {
        const d = [...xray.regionalExposure]
          .sort((a, b) => b.percentage - a.percentage)
          .map((i) => ({ id: i.region, label: i.region, value: i.percentage }));
        return d.length ? (
          <XRayPieChart data={d} categoryLabel={t.region} {...commonPieProps} />
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      }
      case "stock_sector": {
        const d = xray.globalStockSector.map((i) => ({
          id: i.sector,
          label: i.sector,
          value: i.percentage,
        }));
        return d.length ? (
          <XRayPieChart data={d} categoryLabel={t.stockSector} {...commonPieProps} />
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      }
      case "bond_sector": {
        const d = xray.globalBondSector.map((i) => ({
          id: i.sector,
          label: i.sector,
          value: Math.abs(i.percentage),
        }));
        return d.length ? (
          <XRayPieChart data={d} categoryLabel={t.bondSector} {...commonPieProps} />
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      }
      case "equity_style":
        return xray.styleBox.length ? (
          <EquityStyleGrid
            data={xray.styleBox}
            timestamp={xray.timestamp}
            language={userLanguage}
            coverage={xray.coverage}
          />
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      case "top_holdings":
        return xray.topHoldings.length ? (
          <div className="space-y-2">
            {xray.topHoldings.slice(0, 10).map((h) => (
              <div key={`${h.name}-${h.isin}`} className="border-b pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{h.name}</span>
                  <span className="text-sm font-semibold">{h.weight.toFixed(2)}%</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                  <span>{getCountryNameWithFlag(h.country, userLanguage)}</span>
                  <span>{h.sector && h.sector !== "N/A" ? h.sector : ""}</span>
                </div>
              </div>
            ))}
            {xray.timestamp && (
              <div className="text-xs text-muted-foreground pt-2">
                {t.snapshotTaken(getRelativeTime(xray.timestamp, userLanguage))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t.noDataForView}</div>
        );
      default:
        return null;
    }
  };

  // Raw vendor ESG data from the linked instrument's metadata (SFDR + the two
  // sector maps). Absent for instruments without Morningstar coverage (e.g.
  // twelve-data ETFs).
  const mm = metadata as Record<string, unknown> | null;
  const eusfdrType = typeof mm?.eusfdrType === "number" ? (mm.eusfdrType as number) : null;
  const esgExclusions = (mm?.esgExclusions as Record<string, boolean> | undefined) ?? {};
  const esgInvolvement = (mm?.esgInvolvement as Record<string, boolean> | undefined) ?? {};
  const hasSustainability =
    eusfdrType != null ||
    Object.keys(esgExclusions).length > 0 ||
    Object.keys(esgInvolvement).length > 0;

  const sectorLabel = (key: string): string => {
    const entry = EXCLUSION_CATALOG[key as keyof typeof EXCLUSION_CATALOG];
    if (!entry) return key;
    return entry.defaultLabelI18n[userLanguage] ?? entry.defaultLabelI18n.en_GB ?? key;
  };

  const renderSustainability = () => {
    if (!hasSustainability) {
      return <div className="text-sm text-muted-foreground">{t.noSustainability}</div>;
    }
    return (
      <div className="space-y-6">
        {/* SFDR */}
        <div className="space-y-1">
          <div className="text-sm font-semibold">{t.sfdr}</div>
          <div className="text-sm">
            {eusfdrType != null ? t.sfdrArticle(String(eusfdrType)) : t.sfdrNone}
          </div>
        </div>

        {/* Exclusions (stated policy) — sparse map. */}
        <div className="space-y-1">
          <div className="text-sm font-semibold">{t.exclusionsHeading}</div>
          <p className="text-xs text-muted-foreground">{t.exclusionsHint}</p>
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 pt-1">
            {SUSTAINABILITY_EXCLUSION_KEYS.map((key) => {
              const reported = key in esgExclusions;
              const excluded = esgExclusions[key] === true;
              return (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span>{sectorLabel(key)}</span>
                  {!reported ? (
                    <span className="text-xs text-muted-foreground">{t.notReported}</span>
                  ) : (
                    <span className={excluded ? "text-green-600" : "text-muted-foreground"}>
                      {excluded ? `✓ ${t.excluded}` : `✗ ${t.notExcluded}`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Product involvement — HAS exposure. */}
        <div className="space-y-1">
          <div className="text-sm font-semibold">{t.involvementHeading}</div>
          <p className="text-xs text-muted-foreground">{t.involvementHint}</p>
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 pt-1">
            {SUSTAINABILITY_EXCLUSION_KEYS.map((key) => {
              const reported = key in esgInvolvement;
              const involved = esgInvolvement[key] === true;
              return (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span>{sectorLabel(key)}</span>
                  {!reported ? (
                    <span className="text-xs text-muted-foreground">{t.notReported}</span>
                  ) : (
                    <span className={involved ? "text-amber-600" : "text-muted-foreground"}>
                      {involved ? `✓ ${t.involved}` : `✗ ${t.notInvolved}`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Historical Return and Sustainability are shown only for a product with a
  // linked instrument — both read from it, so without one they are empty shells.
  // X-Ray views appear only when the product resolved an X-Ray (i.e. has an ISIN
  // Morningstar accepted). Sustainability still shows an empty state when the
  // linked instrument carries no vendor ESG data.
  const tabs: Array<{ value: View; label: string }> = [
    // Statistics sits right after Historical Return and is shown whenever it is (QL-706).
    ...(hasInstrument
      ? [
          { value: "historical" as const, label: t.historicalReturn },
          { value: "statistics" as const, label: t.statistics },
        ]
      : []),
    ...(xray
      ? [
          { value: "asset_allocation" as const, label: t.assetAllocation },
          { value: "country" as const, label: t.country },
          { value: "region" as const, label: t.region },
          { value: "stock_sector" as const, label: t.stockSector },
          { value: "bond_sector" as const, label: t.bondSector },
          { value: "equity_style" as const, label: t.equityStyle },
          { value: "top_holdings" as const, label: t.topHoldings },
        ]
      : []),
    ...(hasInstrument ? [{ value: "sustainability" as const, label: t.sustainability }] : []),
  ];

  // Derive the rendered tab rather than correcting `view` in an effect: the X-Ray
  // query resolves after the dialog opens, so the tab list grows mid-render and a
  // setState-based fixup would fight it.
  const activeView = tabs.some((tab) => tab.value === view) ? view : (tabs[0]?.value ?? null);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{productName}</DialogTitle>
          <DialogDescription>{isin ?? t.na}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">{t.loading}</div>
        ) : activeView === null ? (
          // No linked instrument and no X-Ray (flag off for the tenant, or the
          // Morningstar call failed) — there is no tab worth rendering.
          <div className="py-8 text-center text-sm text-muted-foreground">{t.noXray}</div>
        ) : (
          <div className="space-y-4">
            <InstrumentSyncAlert health={analysis?.instrumentSync ?? null} lang={userLanguage} />
            {/* QL-736 — what an old last price does to the figures, beside the sync warning. */}
            {analysis?.measuredTo && (
              <Alert variant="info">
                <Info />
                <AlertDescription>{t.measuredTo(formatDate(analysis.measuredTo))}</AlertDescription>
              </Alert>
            )}
            <Tabs
              value={activeView}
              onValueChange={(v) => setView(v as View)}
              className="space-y-4"
            >
              <TabsList className="flex w-full justify-start">
                {tabs.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {/* Single content area — mounting the active view only ensures the
                Vega chart measures a real container width (avoids a blank chart).
                min-height keeps the modal a stable size across tabs (incl. empty
                states) so it doesn't jump when switching views. */}
              <div className="min-h-[420px]">
                {activeView === "historical"
                  ? renderHistorical()
                  : activeView === "statistics"
                    ? renderStatistics()
                    : activeView === "sustainability"
                      ? renderSustainability()
                      : renderXray(activeView)}
              </div>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
